import { useQuery } from "@tanstack/react-query";
import { Gauge, Server } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { omnimizerApi, omnimizerKeys } from "../api";
import { gpuSpec } from "../catalog";
import { allocatedGpus, metricValue } from "../state";
import { sumMetrics, type FleetObservability } from "../observability-queries";
import type { OmnimizerDeployment } from "../types";

export function FleetSummary({ items, fleet }: { items: OmnimizerDeployment[]; fleet: FleetObservability }) {
  const { t } = useTranslation();
  const clusters = useQuery({ queryKey: omnimizerKeys.clusters, queryFn: omnimizerApi.listClusters, refetchInterval: 30000 });
  const totals = sumMetrics(fleet.map((f) => f.query.data));
  const gpusInUse = items.reduce((sum, d) => sum + allocatedGpus(d), 0);
  const byType = new Map<string, { models: number; gpus: number }>();
  for (const d of items) {
    const gpus = allocatedGpus(d);
    if (!gpus) continue;
    const type = String(d.resources?.gpu_type ?? "unknown");
    const row = byType.get(type) || { models: 0, gpus: 0 };
    byType.set(type, { models: row.models + 1, gpus: row.gpus + gpus });
  }
  return <section aria-label={t("omnimizer.observability.fleet")} className="flex min-w-0 flex-col gap-3">
    <h2 className="text-lg font-semibold">{t("omnimizer.observability.fleet")}</h2>
    {clusters.error && <p role="alert" className="text-sm text-destructive">{t("omnimizer.common.loadFailed")}</p>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="text-base font-semibold">{t("omnimizer.overview.gpuByType")}</h2>
          {byType.size ? (
            <ul className="flex flex-col gap-3">
              {[...byType.entries()].map(([type, row]) => (
                <li key={type} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 text-sm">
                  <span className="font-medium">
                    {gpuSpec(type)?.label || type}
                    <span className="ms-2 text-xs text-muted-foreground">
                      {gpuSpec(type) ? `${gpuSpec(type)!.memoryGb} GB` : ""}
                    </span>
                  </span>
                  <span className="text-muted-foreground tabular-nums">
                    {t("omnimizer.overview.gpuRow", { models: row.models, gpus: row.gpus })}
                  </span>
                  <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (row.gpus / Math.max(gpusInUse, 1)) * 100)}%` }} />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-4 text-sm text-muted-foreground">{t("omnimizer.overview.noGpu")}</p>
          )}
        </Card>
        <Card className="flex flex-col gap-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Gauge className="size-4 text-primary" aria-hidden />
              {t("omnimizer.overview.liveLoad")}
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">{t("omnimizer.overview.liveLoadNote", { count: fleet.length })}</p>
          <div className="grid grid-cols-2 gap-2.5">
            {(
              [
                ["output_tokens_per_second", 1],
                ["input_tokens_per_second", 1],
                ["running", 0],
                ["queued", 0],
              ] as const
            ).map(([key, digits]) => (
              <div key={key} className="flex flex-col gap-1 rounded-xl border border-border bg-muted/30 p-3">
                <span className="text-xs text-muted-foreground">{t(`omnimizer.overview.load.${key}`)}</span>
                <strong className="text-xl font-semibold tabular-nums">
                  {totals[key] == null ? t("omnimizer.common.noData") : metricValue(totals[key], digits)}
                </strong>
              </div>
            ))}
          </div>
          <h3 className="mt-2 text-sm font-semibold">{t("omnimizer.overview.clustersTitle")}</h3>
          <ul className="flex flex-col divide-y divide-border">
            {(clusters.data || []).map((c) => (
              <li key={c.id} className="flex items-center gap-3 py-2.5 text-sm">
                <Server className="size-4 text-primary" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {(c.supported_gpu_types || []).join(", ") || "—"}
                    {c.region ? ` · ${c.region}` : ""}
                  </span>
                </span>
                <Badge variant={c.status === "ACTIVE" ? "success" : "muted"}>{c.status}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      </div>

  </section>;
}
